import React from 'react';
import { motion } from 'framer-motion';
import { Cloud, Phone, MessageCircle, Utensils, Sparkles, PenLine, PartyPopper, IceCream, Camera } from 'lucide-react';
const offerings = [{
  label: 'Table & Tabletop',
  icon: Utensils,
  accent: 'text-soft-terracotta',
  items: ['Themed kids table & seating', 'Coordinated tableware & linens', 'Styled centerpieces']
}, {
  label: 'Décor & Styling',
  icon: Sparkles,
  accent: 'text-soft-rose',
  items: ['Balloon garlands & accent balloons', 'Styled backdrops', 'Fresh floral styling']
}, {
  label: 'Signage & Details',
  icon: PenLine,
  accent: 'text-powder-blue',
  items: ['Custom welcome sign', 'Personalized party signage', 'Curated or custom themes']
}, {
  label: 'Play',
  icon: PartyPopper,
  accent: 'text-soft-butter',
  items: ['Face painting', 'Character entertainment', 'Bounce house & piñata']
}, {
  label: 'Treats',
  icon: IceCream,
  accent: 'text-soft-rose',
  items: ['Popcorn cart', 'Cotton candy & snow cones', 'Dessert styling']
}, {
  label: 'Capture & Service',
  icon: Camera,
  accent: 'text-powder-blue',
  items: ['Photographer & videographer', 'Setup, teardown & day-of styling', 'Delivery in Santa Barbara']
}];
export function Packages() {
  return <section id="packages" className="py-24 md:py-28 bg-warm-ivory relative" data-id="element-125" data-mp="29y12ss0n0w1i">
      {/* Delicate background decoration */}
      <motion.div animate={{
      y: [0, 15, 0]
    }} transition={{
      repeat: Infinity,
      duration: 8,
      ease: 'easeInOut'
    }} className="absolute top-20 right-20 text-[#75D5EA] z-0" data-id="element-126" data-mp="205vebs9lgwr2">
        <Cloud className="w-12 h-12" strokeWidth={1} data-id="element-127" data-mp="2snpk3ibkgdye" />
      </motion.div>

      <div className="max-w-6xl mx-auto px-6 md:px-12 relative z-10" data-id="element-128" data-mp="2numz3ykuuctt">
        <div className="text-center mb-14" data-id="element-129" data-mp="lyfki05ia7oa">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="md:text-5xl text-soft-terracotta mb-5 font-normal text-[45px] font-sans" data-id="element-130" data-mp="lynxds1mpczh">
            Make a Wish
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-soft-dark max-w-2xl mx-auto font-light font-[Avenir,_sans-serif] text-[18px] leading-relaxed" data-id="element-131" data-mp="36cy0866nx64">
            Every celebration is designed around your child, your theme, and
            your guest count — so we quote each one individually.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-132" data-mp="385ttz28562ai">
          {offerings.map((group, index) => {
          const Icon = group.icon;
          return <motion.div key={group.label} initial={{
            opacity: 0,
            y: 24
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index % 3 * 0.08,
            duration: 0.5
          }} className="flex flex-col h-full rounded-2xl bg-soft-cream border border-soft-terracotta/15 p-8 shadow-sm" data-id="element-133" data-mp="3i3q17ixykd3c" data-mpi={index}>
                <Icon className={`w-6 h-6 mb-5 ${group.accent}`} strokeWidth={1.5} aria-hidden="true" data-id="element-134" data-mp="26gg8zrv0h2dy" />
                <h3 className="text-xs font-medium uppercase tracking-[0.18em] text-soft-muted mb-5 min-h-[1.5rem]" data-id="element-135" data-mp="27zg8kk24kp06">
                  {group.label}
                </h3>
                <ul className="space-y-3" data-id="element-136" data-mp="2hwuomik4zbry">
                  {group.items.map(item => <li key={item} className="text-soft-dark font-light text-[15px] leading-relaxed" data-id="element-137" data-mp="2mtbdjez5tu63">
                      {item}
                    </li>)}
                </ul>
              </motion.div>;
        })}
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="mt-14 text-center" data-id="element-138" data-mp="1z3qtesr9sqv8">
          <p className="text-soft-dark font-light text-base max-w-xl mx-auto leading-relaxed mb-7" data-id="element-139" data-mp="3oq8wimdzecp8">
            Tell us what you're dreaming of and we'll put together a quote made
            just for you.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-140" data-mp="3fnrcmelstw5e">
            <motion.a whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} href="tel:+18057709317" aria-label="Call Wishing Tree Events at 805 770 9317" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-soft-terracotta px-7 py-3.5 text-base font-medium tracking-wide text-white shadow-sm hover-bg-soft-terracotta transition-colors focus:outline-none focus:ring-2 focus:ring-powder-blue focus:ring-offset-2" data-id="element-141" data-mp="3aowjoju4ar7k">
              <Phone className="w-5 h-5" strokeWidth={1.5} aria-hidden="true" data-id="element-142" data-mp="2i10stvr8btip" />
              (805) 770-9317
            </motion.a>
            <motion.a whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} href="sms:+18057709317" aria-label="Send a text message to Wishing Tree Events at 805 770 9317" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-white px-7 py-3.5 text-base font-medium tracking-wide text-soft-terracotta border border-soft-terracotta/25 shadow-sm hover:bg-soft-terracotta hover:text-white hover:border-soft-terracotta transition-colors focus:outline-none focus:ring-2 focus:ring-powder-blue focus:ring-offset-2" data-id="element-143" data-mp="28cdag9auf5l5">
              <MessageCircle className="w-5 h-5" strokeWidth={1.5} aria-hidden="true" data-id="element-144" data-mp="2mbwjd4bhakyi" />
              Text Us
            </motion.a>
          </div>
        </motion.div>
      </div>
    </section>;
}